import { useFormik } from 'formik'
import { useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  useAddTaskTemplateMutation,
  useGetTaskTypeQuery,
  useUpdateTaskTemplateMutation,
  useGetTaskTemplatesQuery,
  RTK_QUERY_OPTIONS,
} from 'apiv2'
import { prepareCommonSelects } from 'helpers/prepares'
import { closeTemplatesModal, templatesModalSelector } from 'store/modals'
import ConfirmModal from 'ui/components/ConfirmModal'
import ModalBasic from 'ui/components/ModalBasic'
import { notifyError, notifySuccess } from 'ui/components/Notify'
import Form from './components/Form'
import { validationSchema } from './components/Form/validationSchema'
import { MODAL_TYPE } from './constants'

const ModalTemplates = () => {
  const dispatch = useDispatch()
  const [confirmOpened, setConfirmOpened] = useState(false)
  const { open, title, templateId, type } = useSelector(templatesModalSelector)

  const { template } = useGetTaskTemplatesQuery(undefined, {
    skip: !open || !templateId,
    selectFromResult: ({ data }) => ({
      template:
        (data ?? []).find((template) => template.id === templateId) ?? null,
    }),
  })

  const isEditMode = type === MODAL_TYPE.EDIT

  const [addTaskTemplate, { isLoading: addTaskTemplateLoading }] =
    useAddTaskTemplateMutation()
  const [updateTaskTemplate, { isLoading: updateTaskTemplateLoading }] =
    useUpdateTaskTemplateMutation()
  const { data: taskTypesList } = useGetTaskTypeQuery(
    undefined,
    RTK_QUERY_OPTIONS.DISABLE_REFETCH_ON_FOCUS
  )

  const isLoading = addTaskTemplateLoading || updateTaskTemplateLoading

  const taskTypeOptions = useMemo(
    () => prepareCommonSelects(taskTypesList || []),
    [taskTypesList]
  )

  const initialValues = useMemo(() => {
    const selectedTaskType =
      template?.task_type?.id != null
        ? taskTypeOptions.find(
            (option) => option.id === template.task_type.id
          ) || null
        : null

    return {
      task_type: selectedTaskType,
      name: template?.name || '',
      content: template?.content || '',
    }
  }, [template, taskTypeOptions])

  const closeModal = () => {
    dispatch(closeTemplatesModal())
    setConfirmOpened(false)
  }

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema: validationSchema(),
    onSubmit: async (values) => {
      try {
        const params = {
          task_type: values.task_type.id,
          name: values.name.trim(),
          content: values.content,
        }

        if (isEditMode) {
          await updateTaskTemplate({
            templateId,
            params,
          }).unwrap()

          notifySuccess('Шаблон успешно обновлён')
        } else {
          await addTaskTemplate(params).unwrap()

          notifySuccess('Шаблон успешно добавлен')
        }

        formik.resetForm()
        closeModal()
      } catch (e) {
        notifyError(
          isEditMode ? 'Ошибка обновления шаблона' : 'Ошибка добавления шаблона'
        )
      }
    },
  })

  const requestClose = () => {
    if (!formik.dirty) return closeModal()
    setConfirmOpened(true)
  }

  const confirmClose = () => {
    formik.resetForm()
    closeModal()
  }

  return (
    <ModalBasic
      size="large"
      open={open}
      handlerClose={requestClose}
      title={title}
    >
      <Form
        formik={formik}
        isLoading={isLoading}
        taskTypeOptions={taskTypeOptions}
        onClose={requestClose}
      />

      <ConfirmModal
        title="Закрыть окно?"
        open={confirmOpened}
        confirmHandler={confirmClose}
        handlerClose={() => setConfirmOpened(false)}
      />
    </ModalBasic>
  )
}

export default ModalTemplates
